import classNames from "classnames";
import { Clock } from "lucide-react";
import type { ReactNode } from "react";

import type { FilterDefinition } from "./FilterDefinition";
import {
  comparatorsByType,
  type FilterComparator,
  presetsByType,
} from "./FilterSchema";
import styles from "./OperatorPicker.module.css";
import { comparatorLabel, spokenComparatorLabel } from "./operators";

interface OperatorPickerProps {
  fieldType: FilterDefinition["type"];
  value: FilterComparator;
  onChange: (next: FilterComparator) => void;
}

/**
 * Pill-shaped row of operator buttons keyed off the field's data type.
 * Reads the operator set from `operatorsByType[fieldType]` and the per-type
 * display label from `comparatorLabel`; the selected pill is filled, the
 * rest are quiet outlines. Numeric symbol labels (=, ≠, ≤, …) get a spoken
 * `aria-label` so screen readers announce them deterministically.
 * (browser default for buttons).
 */
export function OperatorPicker({
  fieldType,
  value,
  onChange,
}: OperatorPickerProps) {
  const operators = comparatorsByType[fieldType];
  const presets = presetsByType[fieldType];

  return (
    <div className={styles.picker} role="radiogroup" aria-label="Operator">
      <div className={styles.row}>
        {operators.map((operator) => (
          <Pill
            key={operator}
            operator={operator}
            fieldType={fieldType}
            isSelected={operator === value}
            onSelect={() => onChange(operator)}
          />
        ))}
      </div>
      {presets.length > 0 ? (
        <div className={styles.row}>
          {presets.map((operator) => (
            <Pill
              key={operator}
              operator={operator}
              fieldType={fieldType}
              isSelected={operator === value}
              onSelect={() => onChange(operator)}
              className={styles.pillPreset}
              icon={<Clock className={styles.presetIcon} aria-hidden="true" />}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface PillProps {
  operator: FilterComparator;
  fieldType: FilterDefinition["type"];
  isSelected: boolean;
  onSelect: () => void;
  /** Caller-applied class composed with the base pill / selected styles. */
  className?: string;
  /** Optional leading icon rendered before the label (e.g. presets show a clock). */
  icon?: ReactNode;
}

function Pill({
  operator,
  fieldType,
  isSelected,
  onSelect,
  className,
  icon,
}: PillProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      // Numeric pills render as symbols (=, ≠, ≤, …) which screen
      // readers narrate inconsistently. Always provide an explicit
      // phrase form so SR users get deterministic announcement.
      aria-label={spokenComparatorLabel(fieldType, operator)}
      className={classNames(
        styles.pill,
        { [styles.pillSelected]: isSelected },
        className,
      )}
      onClick={onSelect}
    >
      {icon}
      {comparatorLabel(fieldType, operator)}
    </button>
  );
}
